import type {
    PlayerMetaEpisode,
    PlayerMetaFilm,
    PlayerMetaOther,
} from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import {
    playerMetaQuery,
    playerStreamMetaQuery,
    PlayerStreamMeta,
} from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { createResourceQuery } from '@funtech-int/rival';
import { useResources } from '@funtech-int/rival-react';
import { GraphQLError } from 'graphql';

import { GraphqlClient } from '@/shared/lib/graphqlClient';
import { useGraphqlClient } from '@/shared/hooks/useGraphqlClient';
import { PlaybackError } from '@/scenarios/player/lib/PlaybackError';

interface PlayerMetaQueryContext {
    client: GraphqlClient;
    variables: { contentId: string };
}

interface PlayerStreamMetaQueryContext {
    client: GraphqlClient;
    variables: { contentId: string; prerollsSupported: boolean };
}

type PlayerMetaData = PlayerMetaFilm | PlayerMetaEpisode | PlayerMetaOther;

interface PlayerMetaResourceResult {
    data: PlayerMetaData | null | undefined;
    errors?: GraphQLError[];
    requestId: string;
}

interface PlayerStreamMetaResourceResult {
    data: PlayerStreamMeta | null | undefined;
    errors?: GraphQLError[];
    requestId: string;
}

const PLAYER_STREAM_META_LIFETIME = 1000 * 60 * 60;

const playerMetaResourceQuery = createResourceQuery<
    PlayerMetaQueryContext,
    PlayerMetaResourceResult | null
>({
    key(ctx) {
        return [playerMetaQuery.name, ctx.variables.contentId].join(':');
    },

    fetch(ctx) {
        return ctx.client.fetch({
            operation: playerMetaQuery,
            variables: ctx.variables,
        });
    },

    toJSON(response: any) {
        return {
            data: response.data,
            errors: response.errors,
            requestId: response.requestId,
        };
    },

    hydrate(serialized: any) {
        const { data, errors, requestId } = serialized;
        return {
            data: playerMetaQuery.hydrate(data),
            errors,
            requestId,
        };
    },
});

const playerStreamMetaResourceQuery = createResourceQuery<
    PlayerStreamMetaQueryContext,
    PlayerStreamMetaResourceResult | null
>({
    key(ctx) {
        return [
            playerStreamMetaQuery.name,
            ctx.variables.contentId,
            String(ctx.variables.prerollsSupported),
        ].join(':');
    },

    fetch(ctx) {
        return ctx.client.fetch({
            operation: playerStreamMetaQuery,
            variables: ctx.variables,
        });
    },

    toJSON(response: any) {
        return {
            data: response.data,
            errors: response.errors,
            requestId: response.requestId,
        };
    },

    hydrate(serialized: any) {
        const { data, errors, requestId } = serialized;

        return {
            data: playerStreamMetaQuery.hydrate(data),
            errors,
            requestId,
        };
    },
});

function getPlaybackErrorFromResponse(
    response: { errors?: GraphQLError[]; requestId: string } | null | undefined
): PlaybackError | undefined {
    const firstError = response?.errors?.[0];
    if (!firstError) return undefined;

    const ext = firstError.extensions ?? {};
    const errorCode =
        (typeof ext.reason === 'string' && ext.reason) ||
        (typeof ext.type === 'string' && ext.type) ||
        (typeof ext.classification === 'string' && ext.classification) ||
        'UNKNOWN_GRAPHQL_ERROR';

    return new PlaybackError(errorCode, response!.requestId, firstError.message);
}

interface UsePlayerDataOptions {
    prerollsSupported?: boolean;
}

export function usePlayerData(contentId: string, options?: UsePlayerDataOptions) {
    const prerollsSupported = options?.prerollsSupported ?? false;
    const client = useGraphqlClient();

    const [playerMeta, playerStreamMeta] = useResources(
        playerMetaResourceQuery({
            client,
            variables: { contentId },
        }),
        playerStreamMetaResourceQuery(
            {
                client,
                variables: { contentId, prerollsSupported },
            },
            { lifetime: PLAYER_STREAM_META_LIFETIME }
        )
    );

    const error =
        getPlaybackErrorFromResponse(playerMeta) ?? getPlaybackErrorFromResponse(playerStreamMeta);

    return {
        playerMeta: playerMeta?.data ?? undefined,
        playerStreamMeta: playerStreamMeta?.data ?? undefined,
        error,
    };
}
